import React from 'react';

import { useTheme } from '@mui/joy';

interface GimbalIndicatorProps {
  size?: number;
}

/**
 * Creates a gimbal/camera direction indicator as an SVG component.
 * The arrow shows the direction the camera/gimbal is pointing.
 */
const GimbalIndicator: React.FC<GimbalIndicatorProps> = ({ size = 64 }) => {
  const theme = useTheme();
  // Calculate aspect ratio from original SVG (61x67)
  const aspectRatio = 61 / 67;
  const width = size * aspectRatio;
  const height = size;

  // Get colors with fallbacks for when crew palette is not available
  const grayColor = theme.palette.neutral[200] || '#D9D9D9';
  const yellowColor = (theme.palette as any).crew?.batteryYellow || '#F5D907';

  // Use unique gradient ID to avoid conflicts when multiple instances are rendered
  const gradientId = React.useMemo(() => `paint0_linear_gimbal_${Math.random().toString(36).substr(2, 9)}`, []);

  return (
    <svg width={width} height={height} viewBox='0 0 61 67' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path d='M29.1628 67L61 0H29.1628H0L29.1628 67Z' fill={`url(#${gradientId})`} transform='rotate(180 30.5 33.5)' />
      <defs>
        <linearGradient id={gradientId} x1='30.5' y1='0' x2='30.5' y2='67'
gradientUnits='userSpaceOnUse'>
          <stop stopColor={grayColor} stopOpacity='0' />
          <stop offset='1' stopColor={yellowColor} />
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * Utility function to create a gimbal indicator image element
 * that can be used directly with MapDroneView
 * Converts SVG to an HTMLImageElement for use as a map marker
 *
 * Note: Uses data URL instead of blob URL to avoid issues with URL revocation
 */
export const createGimbalIndicatorImage = (size = 64, color?: string): HTMLImageElement => {
  // Calculate aspect ratio from original SVG (61x67)
  const aspectRatio = 61 / 67;
  const width = size * aspectRatio;
  const height = size;

  // Use fallback values since this is a utility function (can't use hooks)
  // These values match the theme defaults
  const grayColor = '#D9D9D9';
  const yellowColor = color || '#F5D907';

  // Create SVG string with unique gradient ID to avoid conflicts
  const gradientId = `paint0_linear_gimbal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const svgString = `<svg width="${width}" height="${height}" viewBox="0 0 61 67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M29.1628 67L61 0H29.1628H0L29.1628 67Z" fill="url(#${gradientId})" transform="rotate(180 30.5 33.5)"/><defs><linearGradient id="${gradientId}" x1="30.5" y1="0" x2="30.5" y2="67" gradientUnits="userSpaceOnUse"><stop stop-color="${grayColor}" stop-opacity="0"/><stop offset="1" stop-color="${yellowColor}"/></linearGradient></defs></svg>`;

  // Convert SVG to data URL (more reliable than blob URL for map markers)
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

  // Create image element
  const img = new Image();
  img.src = svgDataUrl;
  img.width = width;
  img.height = height;

  return img;
};

export default GimbalIndicator;
